"use client";

import { useEffect, useState, type ReactNode } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useLocale } from "next-intl";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import LottieLoader from "@/components/LottieLoader";
import GoogleTagManager from "@/components/GoogleTagManager";
import MetaPixel from "@/components/MetaPixel";
import { useCurrentUser } from "@/modules/auth/context/CurrentUserContext";
import { getRegionLocaleHref } from "@/services/region";
import { consumePostLoginRedirect } from "@/modules/auth/utils/postLoginRedirect";
import { getSafeRedirectParam } from "@/modules/auth/utils/loginRedirect";
import { markRegionNavigationHandled } from "@/modules/auth/utils/regionNavigationLock";
import { isTeacherAccount } from "@/modules/auth/utils/userAccess";
import {
  FREE_TRIAL_PATH,
  isFreeTrialPath,
  needsProfileCompletion,
} from "@/modules/auth/utils/profileCompletion";
import {
  invalidateRecaptchaCallback,
  nextRecaptchaInstanceId,
  recaptchaContainerId,
  recaptchaLoadCallbackName,
  recaptchaScriptId,
} from "@/modules/auth/utils/recaptchaLifecycle";
// import "@/public/f/css/auth.css";


export default function AuthLayout({ children }: { children: ReactNode }) {
  const { user, loading } = useCurrentUser();
  const router = useRouter();
  const locale = useLocale();
  const pathname = usePathname();

  useEffect(() => {
    if (!user) return;

    // The single place that navigates away from an auth page once a user is
    // authenticated - covers both a fresh login submit (LoginForm stashes
    // its intended destination via setPostLoginRedirect() instead of
    // navigating itself, since a second, competing router.replace() call
    // here would race it) and landing on an auth page while already signed
    // in (e.g. a bookmarked /login), which falls back to the locale home.
    // A `?redirect=` param (set by whichever guard/link sent the user here)
    // takes precedence: it's a full, already region+locale-prefixed internal
    // path, so navigate to it directly. sanitizeReturnPath (inside
    // getSafeRedirectParam) is the open-redirect guard. If the user's role
    // can't access it, the destination's own route guard bounces them to their
    // correct dashboard - so no permission logic is duplicated here.
    const returnTo = getSafeRedirectParam(
      typeof window !== "undefined" ? window.location.search : null,
    );
    // Consume the stashed default regardless, so it never leaks into a later
    // sign-in. Used only when there's no explicit return path.
    const redirectTo = consumePostLoginRedirect();
    const regionSlug = (user.region as { slug?: string } | undefined)?.slug;
    markRegionNavigationHandled();

    // Default landing spot when there's no explicit return path: teacher
    // accounts go straight to their dashboard (they have no whos-learning/home
    // flow), otherwise honor the entry point's stashed default (`/student` for
    // the student login page, `/` for the generic login which keeps the student
    // whos-learning modal on the home page). Keyed off the account's immutable
    // account_type — a teacher account has no student side.
    const defaultPath = isTeacherAccount(user)
      ? "/teacher"
      : (redirectTo ?? "/student");

    // An account that still has its profile to fill in - every fresh sign-up,
    // whether by email or Google - goes to the free-trial wizard, whose first
    // step asks for exactly what is missing. That outranks any return path,
    // unless the return path is already the free-trial page, whose query
    // string (e.g. a preselected category) is worth keeping.
    if (needsProfileCompletion(user) && !isFreeTrialPath(returnTo)) {
      router.replace(getRegionLocaleHref(pathname, locale, FREE_TRIAL_PATH, regionSlug));
      return;
    }

    router.replace(
      returnTo ?? getRegionLocaleHref(pathname, locale, defaultPath, regionSlug),
    );
  }, [user, locale, router, pathname]);

  // Tracking follows the prospective *student* through the funnel, so these
  // pages carry the container: register, verify, login, the password flows and
  // /student/login. `/teacher/login` is the one teacher-facing route in this
  // group and stays excluded, same as the teacher dashboard.
  //
  // The pathname is matched on a `teacher` path segment rather than a prefix
  // because a region slug may sit in front of the locale (`/eg/en/...`), the
  // same reason getRegionLocaleHref() exists.
  const isTeacherAuthRoute = /(?:^|\/)teacher(?:\/|$)/.test(pathname);
  const googleTagManager = isTeacherAuthRoute ? null : (
    <>
      <GoogleTagManager />
      <MetaPixel />
    </>
  );

  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? "";
  const recaptchaEnabled = process.env.NEXT_PUBLIC_RECAPTCHA_ENABLED !== "false";

  // Unique per mount: this instance's script tag, render container and load
  // callback can never collide with - or be revived by - a previous or
  // subsequent AuthLayout instance's leftovers. The lazy initializer runs
  // once, on mount, and stays stable across re-renders.
  const [instanceId] = useState(() => nextRecaptchaInstanceId());
  const recaptchaScriptIdValue = recaptchaScriptId(instanceId);
  const recaptchaCallbackName = recaptchaLoadCallbackName(instanceId);
  const recaptchaContainerElementId = recaptchaContainerId(instanceId);

  useEffect(() => {
    // Runs on unmount - i.e. once the user has actually left every route
    // under (auth), not on the loading/authenticated state flips this
    // provider is deliberately kept mounted through (see below). Neutralizes
    // this instance's load callback immediately, so a Google script load
    // that finishes after this point can't call grecaptcha.render() and
    // recreate the badge on whatever page the user has navigated to.
    return () => {
      invalidateRecaptchaCallback(recaptchaCallbackName);
    };
  }, [recaptchaCallbackName]);

  const content = loading ? (
    <LottieLoader centered fullScreen label="Loading..." />
  ) : user ? null : (
    children
  );

  // Disabled via the kill switch, or no key configured (local dev without
  // .env) - skip the provider.
  if (!recaptchaEnabled || !siteKey) {
    return (
      <>
        {googleTagManager}
        {content}
      </>
    );
  }

  // The provider must wrap the loading/authenticated branches too, not sit
  // behind early returns for them: any flip of `loading` would otherwise
  // unmount it, and its cleanup wipes `window.___grecaptcha_cfg` and removes
  // the injected script while leaving `window.grecaptcha` in place. After
  // that, executeRecaptcha() can return a promise that never settles, which
  // is what froze a second login attempt. Mounted once here, it survives
  // every state change on the auth pages.
  //
  // `container.element` points at the div below, which lives inside this
  // component's own tree instead of Google's default (an unscoped badge
  // appended straight to document.body). That does two things: it puts the
  // badge render under React's control, so leaving this route unmounts the
  // div - and any badge inside it - automatically instead of relying on the
  // library's manual DOM surgery; and it switches the script to explicit
  // render mode, so the badge is only ever created by *our* load callback
  // (`recaptchaCallbackName`) actually running - the one the effect above
  // neutralizes the instant this provider unmounts. Fixed-position styling
  // on the div keeps it visually identical to the default floating badge.
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={siteKey}
      scriptProps={{ id: recaptchaScriptIdValue, onLoadCallbackName: recaptchaCallbackName }}
      container={{ element: recaptchaContainerElementId, parameters: {} }}
    >
      {googleTagManager}
      {content}
      <div
        id={recaptchaContainerElementId}
        className="fixed bottom-3.5 end-3.5 z-50"
        aria-hidden="true"
      />
    </GoogleReCaptchaProvider>
  );
}
